import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  FileText,
  Truck,
  ShoppingCart,
  BookOpen,
  Users,
  CheckSquare,
  DollarSign,
  LogOut, 
  Menu, 
  X,
  User,
  Clock,
  Smartphone,
  Wifi,
  WifiOff,
  CalendarCheck,
  Wallet
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { useSocket } from '../contexts/SocketContext';
import MobilePairingDialog from './MobilePairingDialog';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showPairingDialog, setShowPairingDialog] = useState(false);
  const { isConnected, mobileConnected } = useSocket();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Determine current firm from URL
  const getCurrentFirm = () => {
    if (location.pathname.startsWith('/pharmacy')) return 'pharmacy';
    if (location.pathname.startsWith('/traders')) return 'traders';
    return 'pharmacy';
  };
  
  const [currentFirm, setCurrentFirm] = useState(getCurrentFirm());

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Update firm when URL changes
  useEffect(() => {
    setCurrentFirm(getCurrentFirm());
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleFirmSwitch = (firm) => {
    setCurrentFirm(firm);
    if (firm === 'pharmacy') {
      navigate('/pharmacy/ac-statement');
    } else {
      navigate('/traders/todo');
    }
  };

  const pharmacyMenuItems = [
    { icon: FileText, label: 'A/C Statement', path: '/pharmacy/ac-statement' },
    { icon: Truck, label: 'Pending Deliveries', path: '/pharmacy/deliveries' },
    { icon: ShoppingCart, label: 'Sale / Purchase', path: '/pharmacy/sale-purchase' },
    { icon: BookOpen, label: 'Khata Book', path: '/pharmacy/khata' },
  ];

  const tradersMenuItems = [
    { icon: CheckSquare, label: 'TODO List', path: '/traders/todo' },
    { icon: Users, label: 'CRM', path: '/traders/crm' },
    { icon: CalendarCheck, label: 'Staff Attendance', path: '/traders/attendance' },
    { icon: Wallet, label: 'Expenses', path: '/traders/expenses' },
  ];

  const menuItems = currentFirm === 'pharmacy' ? pharmacyMenuItems : tradersMenuItems;

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-IN', { 
      weekday: 'short',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const NavItems = ({ mobile = false }) => (
    <nav className="space-y-1">
      {menuItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        return (
          <button
            key={item.path}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => {
              navigate(item.path);
              if (mobile) setMobileOpen(false);
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActive 
                ? 'bg-blue-50 text-blue-600' 
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
            }`}
          >
            <Icon className="h-5 w-5" />
            <span className="font-medium">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-slate-200 pt-5 pb-4 overflow-y-auto">
          {/* Firm Switcher */}
          <div className="px-4 mb-6">
            <Tabs value={currentFirm} onValueChange={handleFirmSwitch} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="pharmacy" data-testid="firm-switch-pharmacy">Pharmacy</TabsTrigger>
                <TabsTrigger value="traders" data-testid="firm-switch-traders">Traders</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="mt-3 text-center">
              <h2 className="text-lg font-heading tracking-tight text-slate-900">
                {currentFirm === 'pharmacy' ? 'Shri Kripa Pharmacy' : 'Shri Kripa Traders'}
              </h2>
            </div>
          </div>

          {/* Clock */}
          <div className="px-4 mb-6">
            <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-200">
              <div className="flex items-center justify-center gap-2 text-slate-600 mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">Current Time</span>
              </div>
              <div className="text-2xl font-heading tracking-tight text-slate-900 mb-1">
                {formatTime(currentTime)}
              </div>
              <div className="text-xs text-slate-500">
                {formatDate(currentTime)}
              </div>
            </div>
          </div>

          {/* Mobile Connection Status */}
          <div className="px-4 mb-4">
            <Button
              onClick={() => setShowPairingDialog(true)}
              className={`w-full h-12 ${
                mobileConnected 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}
            >
              {mobileConnected ? (
                <>
                  <Wifi className="h-4 w-4 mr-2" />
                  Mobile Connected
                </>
              ) : (
                <>
                  <Smartphone className="h-4 w-4 mr-2" />
                  Pair Mobile Device
                </>
              )}
            </Button>
          </div>

          <div className="mt-2 flex-1 flex flex-col px-3">
            <NavItems />
          </div>
          
          <div className="flex-shrink-0 flex border-t border-slate-200 p-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-3 w-full hover:bg-slate-50 p-2 rounded-lg transition-colors"
                >
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-slate-900">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem 
                  data-testid="logout-button"
                  onClick={handleLogout}
                  className="text-red-600 cursor-pointer"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h2 className="text-lg font-heading tracking-tight text-slate-900">
              {currentFirm === 'pharmacy' ? 'SK Pharmacy' : 'SK Traders'}
            </h2>
            <div className="text-xs text-slate-500">
              {formatTime(currentTime)}
            </div>
          </div>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                data-testid="mobile-menu-button"
                className="lg:hidden"
              >
                {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64 p-0">
              <div className="flex flex-col h-full bg-white">
                <div className="p-6 border-b border-slate-200">
                  <Tabs value={currentFirm} onValueChange={handleFirmSwitch} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="pharmacy">Pharmacy</TabsTrigger>
                      <TabsTrigger value="traders">Traders</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
                <div className="flex-1 p-3">
                  <NavItems mobile={true} />
                </div>
                <div className="border-t border-slate-200 p-4">
                  <Button 
                    data-testid="mobile-logout-button"
                    onClick={handleLogout}
                    variant="outline"
                    className="w-full border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Main content */}
      <main className="lg:pl-64">
        <div className="py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>

      {/* Mobile Pairing Dialog */}
      <MobilePairingDialog 
        open={showPairingDialog} 
        onOpenChange={setShowPairingDialog}
      />
    </div>
  );
};

export default Layout;